import React, { type JSX } from "react";
import { useNavigate } from "react-router";
import type { ConversationSummary } from "../types";
import { getTotalCostFromApi, formatCost } from "../modelPricing";
import { channelMeta } from "../channelMeta";
import { formatDate } from "../../../utils/formatDate";
import CopyConversationButton from "./CopyConversationButton";

const HEADER_CLASS =
  "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
const CELL_CLASS = "px-6 py-4 whitespace-nowrap text-sm text-gray-900";

const COLUMNS = [
  "Conversation ID",
  "Username",
  "Entra OID",
  "Environment",
  "Source",
  "Created At",
  "Updated At",
  "Turns",
  "Duration",
  "Cost",
  "Status",
  "Actions",
];

const ENVIRONMENT_CLASSES: Record<string, string> = {
  prod: "bg-red-100 text-red-800",
  dev: "bg-blue-100 text-blue-800",
  staging: "bg-yellow-100 text-yellow-800",
};

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60000;

/** Scale a duration to the unit that reads best. */
function formatDuration(milliseconds: number): string {
  if (!milliseconds || milliseconds === 0) return "—";
  if (milliseconds < MS_PER_SECOND) return `${milliseconds}ms`;
  if (milliseconds < MS_PER_MINUTE) return `${(milliseconds / MS_PER_SECOND).toFixed(1)}s`;
  return `${(milliseconds / MS_PER_MINUTE).toFixed(1)}m`;
}

/** Total cost, with the token count underneath where it is known. */
function CostCell({ conversation }: { conversation: ConversationSummary }): JSX.Element {
  const tokens =
    (conversation.total_prompt_tokens || 0) + (conversation.total_completion_tokens || 0);

  return (
    <div className="flex flex-col">
      <span className="font-medium">
        {formatCost(
          getTotalCostFromApi(
            conversation.total_input_cost_usd,
            conversation.total_output_cost_usd
          )
        )}
      </span>
      {(conversation.total_prompt_tokens || conversation.total_completion_tokens) && (
        <span className="text-xs text-gray-500">{tokens.toLocaleString()} tokens</span>
      )}
    </div>
  );
}

/** Error, access-denial and feedback markers; a dash where there are none. */
function StatusCell({ conversation }: { conversation: ConversationSummary }): JSX.Element {
  const hasAnyStatus =
    conversation.has_errors ||
    conversation.has_positive_feedback ||
    conversation.has_negative_feedback ||
    conversation.has_sharepoint_denials;

  return (
    <div className="flex items-center space-x-2">
      {conversation.has_errors && (
        <span className="text-red-600" title={`${conversation.error_turns_count} turns with errors`}>
          ⚠️
        </span>
      )}
      {conversation.has_sharepoint_denials && (
        <span className="text-purple-600" title="SharePoint access denied for some documents">
          🔒
        </span>
      )}
      {conversation.has_positive_feedback && (
        <span className="text-green-600" title="Has positive feedback">
          👍
        </span>
      )}
      {conversation.has_negative_feedback && (
        <span className="text-red-600" title="Has negative feedback">
          👎
        </span>
      )}
      {!hasAnyStatus && <span className="text-gray-400">—</span>}
    </div>
  );
}

/** Conversation id, username and the truncated Entra object id. */
function IdentityCells({ conversation }: { conversation: ConversationSummary }): JSX.Element {
  return (
    <>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
        {conversation.conversation_id}
      </td>
      <td className={CELL_CLASS}>{conversation.username}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
        {conversation.entra_oid ? (
          <span title={conversation.entra_oid}>{conversation.entra_oid.substring(0, 8)}...</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </>
  );
}

/** Environment and source pills. */
function OriginCells({ conversation }: { conversation: ConversationSummary }): JSX.Element {
  const meta = channelMeta(conversation.channel_id);

  return (
    <>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            ENVIRONMENT_CLASSES[conversation.environment] || "bg-gray-100 text-gray-800"
          }`}
        >
          {conversation.environment}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${meta.cls}`}>
          {meta.label}
        </span>
      </td>
    </>
  );
}

/** Timestamps, turn count and elapsed time. */
function ActivityCells({ conversation }: { conversation: ConversationSummary }): JSX.Element {
  return (
    <>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(conversation.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(conversation.updated_at)}
      </td>
      <td className={CELL_CLASS}>{conversation.turn_count || 0}</td>
      <td className={CELL_CLASS}>
        <div className="flex items-center space-x-2">
          <span>{formatDuration(conversation.total_time_ms)}</span>
          {conversation.hit_limits && (
            <span className="text-orange-600" title="Rate limits were hit during this conversation">
              🚫
            </span>
          )}
        </div>
      </td>
    </>
  );
}

/** One conversation; a modified click opens it in a new tab. */
function ConversationRow({
  conversation,
  onOpen,
}: {
  conversation: ConversationSummary;
  onOpen: (url: string, newTab: boolean) => void;
}): JSX.Element {
  const url = `/admin/conversation/${conversation.conversation_id}`;

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={(e) => onOpen(url, e.ctrlKey || e.metaKey)}
    >
      <IdentityCells conversation={conversation} />
      <OriginCells conversation={conversation} />
      <ActivityCells conversation={conversation} />
      <td className={CELL_CLASS}>
        <CostCell conversation={conversation} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <StatusCell conversation={conversation} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <div className="inline-flex items-center gap-3">
          <a
            href={url}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            View Details
          </a>
          <CopyConversationButton conversation={conversation} />
        </div>
      </td>
    </tr>
  );
}

/** The conversation list itself, plus its empty state. */
export default function ConversationsTable({
  conversations,
  loading,
}: {
  conversations: ConversationSummary[];
  loading: boolean;
}): JSX.Element {
  const navigate = useNavigate();

  const openConversation = (url: string, newTab: boolean): void => {
    if (newTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      void navigate(url);
    }
  };

  return (
    <div className="bg-white shadow-sm overflow-hidden sm:rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} className={HEADER_CLASS}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {conversations.map((conversation) => (
              <ConversationRow
                key={conversation.conversation_id}
                conversation={conversation}
                onOpen={openConversation}
              />
            ))}
          </tbody>
        </table>
      </div>

      {conversations.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-500">No conversations found matching the current filters.</div>
        </div>
      )}
    </div>
  );
}
